<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ updateMode ? $t('ocspResponderUpdate') : $t('createOcspResponder') }}
        </v-card-title>
        <v-list-item>
          <v-list-item-content>
            <v-form ref="form" v-model="formValid">
              <v-text-field
                id="8a4a3c28"
                v-model="ocspResponder.name"
                :label="$t('name')+`*`"
                :counter="maxCharacters"
                :rules="[rules.required, rules.minMax]"
                :disabled="updateMode"
              ></v-text-field>
              <div v-if="!updateMode" class="text-center d-flex align-center">
                <v-text-field
                  id="bb0040f7"
                  v-model="ocspResponder.subjectDn"
                  :label="$t('subjectDn')+`*`"
                  :counter="maxCharacters"
                  :rules="[rules.required, rules.minMax]"
                  :disabled="updateMode"
                ></v-text-field>
                <v-tooltip v-if="!updateMode" top>
                  <template v-slot:activator="{ on }">
                    <v-icon color="primary" v-on="on">mdi-help-circle</v-icon>
                  </template>
                  <span>{{ $t('helper.subjectDn') }}</span>
                </v-tooltip>
              </div>
              <v-select
                id="0f077bf1"
                v-model="ocspResponder.keyStoreName"
                class="mx-3"
                :items="keyStoreNameList"
                :label="$t('select') +' ' + $t('keyStoreName')+`*`"
                :rules="[rules.required]"
                chips
                :disabled="updateMode"
                @change="keyStoreNameChanged"
              ></v-select>

              <v-row v-if="!updateMode" align="center">
                <v-col>
                  <v-radio-group
                    id="4bc9fc3f"
                    v-model="keyMethod"
                    class="mx-3"
                    row
                    @change="changeVisibleKeyMethodPlatform"
                  >
                    <v-radio
                      :label="$t('keyMethod.generate')"
                      :value="keyMethodList[0]"
                    ></v-radio>
                    <v-radio
                      :label="$t('keyMethod.import')"
                      :value="keyMethodList[1]"
                    ></v-radio>
                    <v-radio
                      :label="$t('keyMethod.exist')"
                      :value="keyMethodList[2]"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row v-if="keyGenerationPlatform" align="center">
                <v-col cols="6" md="2">
                  <v-select
                    id="12f8bc3e"
                    v-model="ocspResponder.generateKey.keyAlg"
                    class="mx-3"
                    :items="keyAlgList"
                    :label="$t('select') + ' ' + $t('keyAlg')"
                    chips
                    :disabled="updateMode"
                    @change="changeDefaultValuesByKeyAlg"
                  ></v-select>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select
                    id="e94b5e98"
                    v-model="ocspResponder.generateKey.keySize"
                    class="mx-3"
                    :items="ocspResponder.generateKey.keyAlg === keyAlgList[1] ? ecKeyNameList : rsaKeySizeList"
                    :label="$t('select') + ' ' + $t('keySize')"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="keyImportPlatform">
                <v-col cols="6" md="1">
                  <input
                    id="7d3c974c"
                    ref="keyUpload"
                    style="display: none"
                    type="file"
                    @change="loadKeyFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.keyUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ !!ocspResponder.importKey.pem ? $t('replace') : $t('upload') }}
                  </v-btn>
                </v-col>
                <v-col cols="6" md="4">
                  <v-text-field
                    id="2c629503"
                    v-model="ocspResponder.importKey.password"
                    :label="$t('keyPassword')"
                    type="password"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="existingKeyPlatform">
                <v-col>
                  <v-select
                    id="15c35a6d"
                    v-model="ocspResponder.keyAlias"
                    class="mx-3"
                    :items="keyAliasList"
                    item-text="text"
                    item-value="value"
                    :label="$t('select') + ' ' + $t('keyAlias')"
                    :rules="[rules.required]"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- loadCaCertificate -->
              <div v-if="!updateMode" class="mx-2">
                <input
                  id="54aa25a4"
                  ref="caCertificateUpload"
                  style="display: none"
                  type="file"
                  accept=".pem, .cer"
                  @change="loadCaCertificateFromFile($event)"
                >
                <v-btn class="primary" @click="$refs.caCertificateUpload.click()">
                  <v-icon left>mdi-upload</v-icon>
                  {{ !!ocspResponder.caCert ? $t('replace') : $t('upload') }} {{ $t('caCert') }}
                </v-btn>
              </div>

              <!-- caCert -->
              <v-row v-if="!!ocspResponder.caCert">
                <v-col>
                  <v-card class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('caCert') }}
                        </v-list-item-subtitle>
                        <v-text-field
                          v-model="ocspResponder.caCert.serialNumber"
                          :label="$t('serialNumber')"
                          :readonly="!!ocspResponder.caCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.caCert.issuer"
                          :label="$t('issuer')"
                          :readonly="!!ocspResponder.caCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.caCert.subject"
                          :label="$t('subject')"
                          :readonly="!!ocspResponder.caCert"
                        ></v-text-field>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.caCert.notBefore)"
                              :label="$t('notBefore')"
                              :readonly="!!ocspResponder.caCert"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.caCert.notAfter)"
                              :label="$t('notAfter')"
                              :readonly="!!ocspResponder.caCert"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <!-- crlUrls -->
              <v-row>
                <v-col cols="4">
                  <v-row>
                    <v-col>
                      <v-btn class="primary mx-2 my-2" @click="addCrlUrl">
                        <v-icon left>mdi-plus</v-icon>
                        {{ $t('add') + ' ' + $t('crlUrl') }}
                      </v-btn>
                    </v-col>
                    <v-col>
                      <v-text-field
                        id="cc0fbfc5"
                        v-model="ocspResponder.crlExpirationMins"
                        :rules="[rules.required, rules.numberFromToValid]"
                        :label="$t('crlExpirationMins')+`*`"
                        min="1"
                        max="1440"
                        type="number"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row id="73362809">
                <v-col v-for="(crlUrl, index) in ocspResponder.crlUrls" :key="index" cols="12">
                  <v-card :id="'73362809' + index" class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('crlUrl') + ' - ' + (index + 1) }}
                        </v-list-item-subtitle>
                        <v-text-field
                          id="fc2f1932"
                          v-model="ocspResponder.crlUrls[index]"
                          :label="$t('crlUrl')+`*`"
                          :counter="maxCharacters"
                          :rules="[rules.required, rules.minMax]"
                        ></v-text-field>
                      </v-list-item-content>
                    </v-list-item>
                    <v-card-actions>
                      <v-btn color="error" @click="removeCrlUrl(index)">{{ $t('delete') }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- downloadCertReq -->
              <v-btn
                v-if="updateMode"
                id="8616cc2c"
                class="primary mx-2 my-2"
                @click="downloadCertReqFile(ocspResponder.certReq.trim())"
              >
                <v-icon left>mdi-download</v-icon>
                {{ $t('download') + ' ' + $t('certReq') }}
              </v-btn>

              <!-- loadSignerCertificate -->
              <div v-if="updateMode" class="mx-2">
                <input
                  id="0f106a3f"
                  ref="signerCertificateUpload"
                  style="display: none"
                  type="file"
                  accept=".pem, .cer"
                  @change="loadSignerCertificateFromFile($event)"
                >
                <v-btn class="green" @click="$refs.signerCertificateUpload.click()">
                  <v-icon left>mdi-upload</v-icon>
                  {{ !!ocspResponder.signerCert ? $t('replace') : $t('upload') }} {{ $t('signerCert') }}
                </v-btn>
              </div>

              <!-- signerCert -->
              <v-row v-if="updateMode && !!ocspResponder.signerCert" id="b27628fb">
                <v-col>
                  <v-card class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('signerCert') }}
                        </v-list-item-subtitle>
                        <v-text-field
                          v-model="ocspResponder.signerCert.serialNumber"
                          :label="$t('serialNumber')"
                          :readonly="!!ocspResponder.signerCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.signerCert.issuer"
                          :label="$t('issuer')"
                          :readonly="!!ocspResponder.signerCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.signerCert.subject"
                          :label="$t('subject')"
                          :readonly="!!ocspResponder.signerCert"
                        ></v-text-field>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.signerCert.notBefore)"
                              :label="$t('notBefore')"
                              :readonly="!!ocspResponder.signerCert"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.signerCert.notAfter)"
                              :label="$t('notAfter')"
                              :readonly="!!ocspResponder.signerCert"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <!-- loadAdditionalCertificate -->
              <div v-if="updateMode" class="mx-2 my-2">
                <input
                  id="62044942"
                  ref="additionalCertificateUpload"
                  style="display: none"
                  type="file"
                  accept=".pem, .cer"
                  @change="loadAdditionalCertificateFromFile($event)"
                >
                <v-btn class="green" @click="$refs.additionalCertificateUpload.click()">
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('upload') + ' ' + $t('additionalCert') }}
                </v-btn>
              </div>

              <!-- additionalCert(s) -->
              <v-row v-if="updateMode" id="142ad149">
                <v-col v-for="(cert, index) in ocspResponder.additionalCerts" :key="index" cols="12">
                  <v-card :id="'142ad149' + index" class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('additionalCert') + ' - ' + (index + 1) }}
                        </v-list-item-subtitle>
                        <v-text-field
                          v-model="ocspResponder.additionalCerts[index].serialNumber"
                          :label="$t('serialNumber')" :readonly="!!ocspResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.additionalCerts[index].issuer"
                          :label="$t('issuer')"
                          :readonly="!!ocspResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-text-field
                          v-model="ocspResponder.additionalCerts[index].subject"
                          :label="$t('subject')"
                          :readonly="!!ocspResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.additionalCerts[index].notBefore)"
                              :label="$t('notBefore')"
                              :readonly="!!ocspResponder.additionalCerts[index]"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(ocspResponder.additionalCerts[index].notAfter)"
                              :label="$t('notAfter')"
                              :readonly="!!ocspResponder.additionalCerts[index]"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                    <v-card-actions>
                      <v-btn color="error" @click="removeAdditionalCert(index)">{{ $t('delete') }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-list-item-content>
        </v-list-item>
        <v-card-actions>
          <v-btn
            id="0b575ded"
            class="ma-2"
            color="primary"
            :loading="btnLoading"
            :disabled="!formValid || btnLoading"
            @click="createOrUpdateOcspResponder"
          >
            {{ updateMode ? $t('update') : $t('create') }}
          </v-btn>
          <v-btn id="cc75f7e2" class="ma-2" @click="back">{{ $t('back') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import keystores from '@/api/keystores'
import signers from '@/api/signers'
import certificates from '@/api/certificates'
import FileSaver from 'file-saver'
import keyMethod from './KeyMethod'
import keyAlgorithm from './KeyAlgorithm'
import rsaKeySize from './RsaKeySize'
import ecKeyName from './EcKeyName'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'CreateOcspResponderOrUpdate',

  data() {
    return {
      formValid: true,
      maxCharacters: 255,
      keyStoreNameList: [],
      keyMethodList: keyMethod,
      keyMethod: keyMethod[0],
      keyAliasList: [],
      keyAlgList: keyAlgorithm,
      rsaKeySizeList: rsaKeySize,
      ecKeyNameList: ecKeyName,
      ocspResponder: {
        crlUrls: [''],
        generateKey: {
          keyAlg: keyAlgorithm[0],
          keySize: rsaKeySize[0]
        },
        importKey: {
          pem: null,
          password: null
        },
        keyAlias: null
      },
      keyGenerationPlatform: true,
      keyImportPlatform: false,
      existingKeyPlatform: false,
      btnLoading: false,
      rules: {
        required: v => !!v || this.$t('validation.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.mustBeLessThan', { max: this.maxCharacters }),
        numberFromToValid: v => (v >= 1 && v <= 1440) || this.$t('validation.numberFromToValid', { min: 1, max: 1440 })
      }
    }
  },

  computed: {
    updateMode() {
      return this.$route.params.ocspResponderId != null
    }
  },

  async mounted() {
    if (this.updateMode) {
      await this.getOcspResponder()
    }
    await this.getKeyStoreNames()
  },

  methods: {
    async getOcspResponder() {
      try {
        this.ocspResponder = await signers.getOcspResponder(this.$route.params.ocspResponderId)
        this.setKeyMethodPlatformValues()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    async createOrUpdateOcspResponder() {
      this.btnLoading = true

      if (!this.$refs.form.validate()) {
        return
      }

      let ocspResponderDto = Object.assign({}, this.ocspResponder, {
        generateKey: this.keyMethod === this.keyMethodList[0] ? this.ocspResponder.generateKey : null,
        importKey: this.keyMethod === this.keyMethodList[1] ? this.ocspResponder.importKey : null,
        keyAlias: this.keyMethod === this.keyMethodList[2] ? this.ocspResponder.keyAlias : null
      })

      try {
        this.updateMode ? await signers.updateOcspResponder(this.$route.params.ocspResponderId, ocspResponderDto) : await signers.createOcspResponder(ocspResponderDto)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'ALREADY_EXISTS':
              errorMsg = this.$t('toastMessage.alreadyExist')
              break
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      } finally {
        this.btnLoading = false
      }
    },

    async getKeyStoreNames() {
      try {
        this.keyStoreNameList = await keystores.getKeyStoreNames()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    changeDefaultValuesByKeyAlg() {
      this.ocspResponder.generateKey.keySize = (this.ocspResponder.generateKey.keyAlg === this.keyAlgList[1] ? this.ecKeyNameList[0] : this.rsaKeySizeList[0])
    },

    changeVisibleKeyMethodPlatform() {
      switch (this.keyMethod) {
        case this.keyMethodList[0]:
          this.configurationOfVisiblePlatforms(true, false, false)
          break
        case this.keyMethodList[1]:
          this.configurationOfVisiblePlatforms(false, true, false)
          break
        case this.keyMethodList[2]:
          this.configurationOfVisiblePlatforms(false, false, true)
          break
      }
    },

    configurationOfVisiblePlatforms(keyGen, keyImp, keyExist) {
      this.keyGenerationPlatform = keyGen
      this.keyImportPlatform = keyImp
      this.existingKeyPlatform = keyExist
    },

    setKeyMethodPlatformValues() {
      const isGenerateKeyEmpty = Object.keys(this.ocspResponder.generateKey).length === 0
      this.keyGenerationPlatform = !isGenerateKeyEmpty
      if (isGenerateKeyEmpty) {
        this.keyAliasList = [{ text: this.ocspResponder.keyAlias, value: this.ocspResponder.keyAlias }]
        this.existingKeyPlatform = this.ocspResponder.keyAlias !== null
      }
    },

    async downloadCertReqFile(data) {
      await FileSaver.saveAs(new Blob([data], { type: 'application/x-pem-file' }), this.ocspResponder.name + '-certReq.pem')
    },

    loadKeyFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => { this.ocspResponder.importKey.pem = event.target.result }
      reader.readAsText(file)
      this.$refs.keyUpload.value = null
    },

    loadCaCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addCaCert(event.target.result)
      reader.readAsText(file)
      this.$refs.caCertificateUpload.value = null
    },

    async addCaCert(value) {
      this.$set(this.ocspResponder, 'caCert', Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    addCrlUrl() {
      this.ocspResponder.crlUrls.push(Object.assign([], ''))
    },

    removeCrlUrl(index) {
      this.ocspResponder.crlUrls.splice(index, 1)
    },

    loadSignerCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addSignerCert(event.target.result)
      reader.readAsText(file)
      this.$refs.signerCertificateUpload.value = null
    },

    async addSignerCert(value) {
      this.$set(this.ocspResponder, 'signerCert', Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    loadAdditionalCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addAdditionalCert(event.target.result)
      reader.readAsText(file)
      this.$refs.additionalCertificateUpload.value = null
    },

    async addAdditionalCert(value) {
      this.ocspResponder.additionalCerts.push(Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    removeAdditionalCert(index) {
      this.ocspResponder.additionalCerts.splice(index, 1)
    },

    async parseCertificate(pem) {
      try {
        return await certificates.parseCertificate(pem)
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
        throw error
      }
    },

    async keyStoreNameChanged() {
      const entries = await keystores.getKeyStoreEntries(this.ocspResponder.keyStoreName)
      this.keyAliasList = entries.flatMap(e => {
        return { text: `${e.keyAlias} (${e.keyIdentifier})`, value: e.keyAlias }
      })
    },

    back() {
      this.$router.go(-1)
    }
  }
}
</script>

<style>
</style>
